import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { orangeBookAPI } from '../services/api';
import recentSearchesManager from '../utils/recentSearches';
import RecentSearchesSidebar from '../components/RecentSearchesSidebar';
import SEO from '../components/SEO';

function OrangeBook() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchType, setSearchType] = useState('both'); // both, ingredient, brand
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const resultsPerPage = 10;

  // Auto-search if query parameter is present
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim()) {
      setSearchQuery(query);
      performSearch(query, searchType, 1);
    }
  }, [searchParams, searchType]);

  const performSearch = async (query, type, page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * resultsPerPage;
      const response = await orangeBookAPI.search(query, type, resultsPerPage, skip);
      setResults(response.data);
      setCurrentPage(page);

      // Calculate total pages based on results
      const totalResults = response.data?.results?.length || 0;
      setTotalPages(totalResults < resultsPerPage ? page : page + 1);

      // Save to recent searches (only on first page)
      if (page === 1) {
        recentSearchesManager.addSearch({
          query: query.trim(),
          database: 'orangebook',
          searchType: type,
          resultsCount: totalResults
        });
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Update URL with search query
    setSearchParams({ q: searchQuery.trim() });

    await performSearch(searchQuery.trim(), searchType, 1);
  };

  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;
    await performSearch(searchQuery.trim(), searchType, page);
  };

  const renderResults = (resultsData, title) => {
    if (!resultsData || !resultsData.results || resultsData.results.length === 0) {
      return null;
    }

    return (
      <div>
        <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>{title}</h3>
        <div className="results-grid">
          {resultsData.results.map((product, index) => (
            <Link 
              to={`/orangebook/${product.application_number}`} 
              key={index} 
              className="clickable-card" 
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="result-card hoverable-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>{product.brand_name || product.openfda?.brand_name?.[0] || 'Unknown'}</h3>
                  <span style={{ 
                    backgroundColor: '#f39c12', 
                    color: 'white', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>FDA OB</span>
                </div>
              
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Product Information</h4>
                <p><strong>Generic Name:</strong> {product.generic_name || product.openfda?.generic_name?.[0] || 'N/A'}</p>
                <p><strong>Manufacturer:</strong> {product.labeler_name || product.openfda?.manufacturer_name?.[0] || 'N/A'}</p>
                <p><strong>Application Number:</strong> {product.application_number || 'N/A'}</p>
                <p><strong>Product Type:</strong> {product.product_type || 'N/A'}</p>
              </div>

              {(product.route || product.dosage_form || product.strength) && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Formulation Details</h4>
                  {product.dosage_form && <p><strong>Dosage Form:</strong> {product.dosage_form}</p>}
                  {product.route && (
                    <p><strong>Route:</strong> {Array.isArray(product.route) ? product.route.join(', ') : product.route}</p>
                  )}
                  {product.strength && <p><strong>Strength:</strong> {product.strength}</p>}
                </div>
              )}

              {(product.marketing_status || product.te_code) && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Regulatory Status</h4>
                  {product.marketing_status && <p><strong>Marketing Status:</strong> {product.marketing_status}</p>}
                  {product.te_code && (
                    <p>
                      <strong>TE Code:</strong> 
                      <span style={{ 
                        marginLeft: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: product.te_code.startsWith('A') ? '#d4edda' : '#f8d7da',
                        color: product.te_code.startsWith('A') ? '#155724' : '#721c24',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        {product.te_code}
                      </span>
                      <span style={{ fontSize: '0.85em', color: '#7f8c8d', marginLeft: '0.5rem' }}>
                        {product.te_code.startsWith('A') ? '(Therapeutically Equivalent)' : '(NOT Therapeutically Equivalent)'}
                      </span>
                    </p>
                  )}
                  {product.reference_listed_drug && (
                    <p><strong>Reference Listed Drug:</strong> {product.reference_listed_drug === 'Yes' ? '✓ Yes' : 'No'}</p>
                  )}
                  {product.reference_standard && (
                    <p><strong>Reference Standard:</strong> {product.reference_standard === 'Yes' ? '✓ Yes' : 'No'}</p>
                  )}
                </div>
              )}

              {product.active_ingredients && product.active_ingredients.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Active Ingredients</h4>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    {product.active_ingredients.map((ing, i) => (
                      <li key={i}>{ing.name} - {ing.strength}</li>
                    ))}
                  </ul>
                </div>
              )}

              {product.openfda?.substance_name && product.openfda.substance_name.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Substance Names</h4>
                  <p>{product.openfda.substance_name.join(', ')}</p>
                </div>
              )}

              {product.openfda?.pharm_class_epc && product.openfda.pharm_class_epc.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Pharmacologic Class</h4>
                  <p>{product.openfda.pharm_class_epc.slice(0, 3).join(', ')}</p>
                </div>
              )}

              {product.openfda?.product_ndc && product.openfda.product_ndc.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>NDC Codes</h4>
                  <p style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
                    {product.openfda.product_ndc.slice(0, 3).join(', ')}
                    {product.openfda.product_ndc.length > 3 && ` (+${product.openfda.product_ndc.length - 3} more)`}
                  </p>
                </div>
              )}
              
              <div className="view-details-link">
                View Full Details →
              </div>
            </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  // SEO structured data
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    'name': 'FDA Orange Book Search',
    'description': 'Search FDA Orange Book for approved drug products with therapeutic equivalence evaluations',
    'specialty': 'Pharmaceutical Sciences'
  };

  return (
    <div className="page-container">
      <SEO
        title="FDA Orange Book Search - Drug Equivalence & Generic Alternatives"
        description="Search the FDA Orange Book for approved drug products with therapeutic equivalence evaluations. Find generic alternatives, reference listed drugs, and therapeutic equivalence information."
        keywords="FDA Orange Book, drug equivalence, generic drugs, reference listed drugs, therapeutic equivalence, AB rating, generic alternatives"
        canonical="/orangebook"
        structuredData={seoStructuredData}
      />
      <h1 className="page-title">FDA Orange Book Search</h1>
      <p className="page-description">
        Search the FDA Orange Book for approved drug products with therapeutic equivalence evaluations. 
        Useful for finding generic alternatives and reference listed drugs.
      </p>

      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="both"
                checked={searchType === 'both'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}Search Both
            </label>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="ingredient"
                checked={searchType === 'ingredient'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}Active Ingredient
            </label>
            <label>
              <input
                type="radio"
                value="brand"
                checked={searchType === 'brand'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}Brand Name
            </label>
          </div>

          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder={
                searchType === 'ingredient'
                  ? 'Enter active ingredient (e.g., ibuprofen)...'
                  : searchType === 'brand'
                  ? 'Enter brand name (e.g., Advil)...'
                  : 'Enter drug name...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">Error: {error}</div>}
      
      {loading && <div className="loading">Loading...</div>}

      {!loading && results && (
        <div className="results-section">
          <div className="results-header">
            <h2>Results</h2>
            {totalPages > 1 && (
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
            )}
          </div>

          {results.ingredient_results && renderResults(results.ingredient_results, 'By Ingredient')}
          {results.brand_results && renderResults(results.brand_results, 'By Brand Name')}
          {(!results.ingredient_results?.results?.length && !results.brand_results?.results?.length) && (
            <p className="no-results">No results found</p>
          )}

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="pagination-btn"
              >
                ← Previous
              </button>

              <div className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      <RecentSearchesSidebar database="orangebook" />

      <div style={{ marginTop: '3rem', padding: '1.5rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>About Therapeutic Equivalence (TE) Codes</h3>
        <p style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          The Orange Book uses therapeutic equivalence codes to indicate whether drugs are considered
          therapeutically equivalent. Codes starting with "A" indicate products considered therapeutically
          equivalent, while "B" codes indicate products NOT considered therapeutically equivalent.
        </p>
      </div>
    </div>
  );
}

export default OrangeBook;
